import clsx from "clsx";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/20/solid";

export interface PaginationProps {
    currentPage: number;
    totalPages: number;
    onPageChange: (page: number) => void;
    className?: string;
    testId?: string;
}

export function Pagination({
    currentPage,
    totalPages,
    onPageChange,
    className,
    testId,
}: PaginationProps) {
    const getVisiblePages = (): number[] => {
        if (totalPages <= 5) {
            return Array.from({ length: totalPages }, (_, i) => i + 1);
        }

        const pages: number[] = [];

        if (currentPage <= 3) {
            pages.push(1, 2, 3, 4, totalPages);
        } else if (currentPage >= totalPages - 2) {
            pages.push(
                1,
                totalPages - 3,
                totalPages - 2,
                totalPages - 1,
                totalPages,
            );
        } else {
            pages.push(
                1,
                currentPage - 1,
                currentPage,
                currentPage + 1,
                totalPages,
            );
        }

        return pages;
    };

    if (totalPages <= 1) return null;

    const visiblePages = getVisiblePages();

    return (
        <div
            className={clsx(
                "flex items-center justify-between gap-1",
                className,
            )}
            data-testid={testId}
        >
            <button
                onClick={() => onPageChange(currentPage - 1)}
                disabled={currentPage <= 1}
                className="secondary sm"
            >
                <ChevronLeftIcon className="size-6" />
            </button>

            <div className="flex items-center gap-2">
                {visiblePages.map((pageNumber) => {
                    const isCurrentPage = pageNumber === currentPage;

                    return (
                        <button
                            key={pageNumber}
                            onClick={() => onPageChange(pageNumber)}
                            className={clsx(
                                "sm",
                                isCurrentPage ? "" : "secondary",
                            )}
                        >
                            {pageNumber}
                        </button>
                    );
                })}
            </div>

            <button
                onClick={() => onPageChange(currentPage + 1)}
                disabled={currentPage >= totalPages}
                className="secondary sm"
            >
                <ChevronRightIcon className="size-6" />
            </button>
        </div>
    );
}
